import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { fetchSiteImages } from "../lib/api";

const STORY_IMG = "/api/images/about-portrait";
const LEAF_IMG = "/api/images/about-banner";

const VALUES = [
  { t: "Small-batch, always", d: "Hand-measured and blended in Racine, Wisconsin. We make what we can care for." },
  { t: "Whole botanicals", d: "Real herbs, flowers, and roots — chosen for how they make you feel, never fillers." },
  { t: "Ritual over rush", d: "We believe a cup of tea is a small ceremony. Everything we make honors the pause." },
];

export default function About() {
  const [img, setImg] = useState({});
  useEffect(() => { fetchSiteImages().then(setImg).catch(() => {}); }, []);
  return (
    <div className="pt-32 md:pt-40">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta mb-6">Our Story</p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-forest tracking-tight leading-[0.95] max-w-4xl">
            A quiet study of <span className="italic text-sage">herbs</span>, steeped over years.
          </h1>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-16 items-center mt-20">
          <Reveal>
            <div className="rounded-[3rem] overflow-hidden">
              <img src={img["about-portrait"] || STORY_IMG} alt="Holding tea" className="w-full h-[560px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="space-y-5 text-forest-soft text-lg leading-relaxed">
              <p>Serendipitous Sips &amp; Wellness started at a kitchen table — a search for calm during a restless season of life.</p>
              <p>What began as a few jars of dried herbs slowly became a devotion: learning which botanicals soothe, which ones energize, and how to blend them into something that feels like being cared for.</p>
              <p>Today, every blend is still handcrafted in small batches in Racine, Wisconsin. We name our teas for feelings because that&rsquo;s how we choose them — by intention, for the moment you need them most.</p>
            </div>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-28">
          {VALUES.map((v, i) => (
            <Reveal key={v.t} delay={i * 0.1}>
              <div className="border-t-2 border-terracotta pt-6">
                <h3 className="font-serif text-3xl text-forest mb-3">{v.t}</h3>
                <p className="text-forest-soft leading-relaxed">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <section className="relative mt-28 h-[420px] overflow-hidden flex items-center">
        <img src={img["about-banner"] || LEAF_IMG} alt="Botanical texture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forest/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <Reveal>
            <h2 className="font-serif text-cream text-4xl md:text-6xl leading-tight max-w-2xl mb-8">
              Looking for something all your own?
            </h2>
            <p className="text-cream/80 text-lg max-w-xl mb-8">Custom blends are available by request — tell us the feeling, and we&rsquo;ll craft the cup.</p>
            <Link to="/contact" data-testid="about-contact-cta" className="inline-flex items-center gap-3 rounded-full bg-cream text-forest hover:bg-terracotta hover:text-cream px-7 py-3.5 text-sm uppercase tracking-widest font-semibold" style={{ transition: "background-color 0.3s ease, color 0.3s ease" }}>
              Request a custom blend <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
